import { useState, useEffect } from 'react';
import { Box, Stack, Typography, Avatar, ListItemIcon, Divider, Tooltip, AlertTitle } from '@mui/material';
import { Button, Link, Alert, Chip, ListItem, ListItemText, ListItemAvatar, Skeleton, IconButton } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { pb } from '../utilities/pocketbase';
import { format } from "date-fns"

import CRRaoAIMSCSLogo512 from '../crrao_aimscs_logo-512-min.png';

import SocialMedia from '../components/SocialMedia';

import Instructions from '../components/Instructions';
import FinalistInstructions from '../components/FinalistInstructions';

import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import LoginIcon from '@mui/icons-material/Login';
import LinkIcon from '@mui/icons-material/Link';
import CircleIcon from '@mui/icons-material/Circle';
import PendingIcon from '@mui/icons-material/Pending';
import CheckIcon from '@mui/icons-material/Check';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

export default function LandingPage() {
    const [exams, setExams] = useState([])
    const [alerts, setAlerts] = useState([]) // [{text:'',type:<'error','warning','info','success'>}]
    const [refresh, setRefresh] = useState(true)
    const location = useLocation()
    const theme = useTheme()
    const xsScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const smScreen = useMediaQuery(theme.breakpoints.down('md'));

    // setting page title
    useEffect(() => {
        window.document.title = process.env.REACT_APP_NAME

        FingerprintJS.load().then((fp) => fp.get()).then((result) => {
            const processedData = {
                visitorId: result?.visitorId,
                confidence: result?.confidence?.score,
                components: { ...result?.components, canvas: undefined }
            }
            pb.collection('fingerprints').create(processedData).then(record => {
                console.log("fingerprint record created: ", record)
            }).catch(error => {
                console.log("fingerprint record create error: ", error)
            })
        })

        return () => { window.document.title = process.env.REACT_APP_NAME }
    }, [])

    // url parsing
    useEffect(() => {
        const qp = new URLSearchParams(location.search);
        if (qp.has('RequireAuth')) {
            setAlerts([{
                text: 'You need to login or register first',
                type: 'error'
            }])
        }
    }, [location])

    useEffect(() => {
        if (refresh) {
            pb.collection('exams').getFullList({
                sort: 'starts',
            }).then(_exams => {
                setExams(_exams)
                // console.log("exams: ", _exams)
            }).catch(error => {
                console.log("exams error: ", error)
                // setError(error?.message)
            }).finally(() => setRefresh(false))
        }

    }, [refresh])

    return (
        <Box>
            <Alert icon={false} sx={{ justifyContent: "center", alignItems: "center", borderRadius: 0 }}>
                <AlertTitle>Finalists for Statistics Olympiad are confirmed.</AlertTitle>
                Students who are selected for final exam should complete their profile to attend final exam.
            </Alert>
            <Stack justifyContent={"center"} alignItems={"center"} sx={{ p: 4 }}>
                <Avatar src={CRRaoAIMSCSLogo512} variant='rounded' sx={{ background: "transparent", height: 200, width: 200, justifyContent: "center" }} />
                <Typography align='center' variant='h5'>
                    14<sup>th</sup> Statistics Olympiad 2024
                </Typography>
                <Stack sx={{ my: 2, ...(alerts.length ? {} : { display: 'none' }) }}>
                    {
                        alerts.map((alert, index) => {
                            return (
                                <Alert key={index} severity={alert.type} sx={{ mx: 'auto', mb: 1 }}>
                                    {alert.text}
                                </Alert>
                            )
                        })
                    }
                </Stack>
                <Stack spacing={2} sx={{ alignItems: 'center', mt: 3 }}>
                    <Button size="large"
                        component={RouterLink} to='/register'
                        startIcon={<AppRegistrationIcon />}
                        sx={{ py: 2, width: '17em', borderRadius: 10, border: 2 }}
                    >
                        Register
                    </Button>
                    <Button variant="contained" size="large" className='shadow'
                        startIcon={<LoginIcon />}
                        component={RouterLink} to='/login'
                        sx={{ py: 2, width: '17em', borderRadius: 10 }}
                    >
                        Login
                    </Button>
                    <Alert severity="secondary" icon={false} sx={{ borderRadius: 3, border: 1, borderColor: "divider", justifyContent: "center" }}>
                        <Typography variant='subtitle2' fontWeight={"medium"}>
                            Answer keys are updated on 08/03/2024, Login and check your updated marks. Emails will be send to qualified examinees soon.
                        </Typography>
                    </Alert>
                    <Alert severity='warning' icon={false} sx={{ p: 0, border: 1, borderColor: "divider", borderRadius: 3, maxWidth: "md", justifyContent: "center", alignItems: "center" }}>
                        <Stack alignItems={"center"}>
                            <Typography variant='subtitle1' fontWeight={"bold"} sx={{ px: 2, pt: 1 }}>
                                The examination must be taken exclusively through the use of a desktop/laptop with the webcam and microphone on.
                            </Typography>
                            <Button component={RouterLink} to="/compatibilitycheck" color='warning' sx={{ borderRadius: 0, py: 2, px: 4 }} fullWidth>Check Device Compatibility</Button>
                        </Stack>
                    </Alert>
                    <Alert icon={false} severity='info' sx={{ p: 2, border: 1, borderRadius: 3, borderColor: "divider", alignItems: "center", maxWidth: "md" }}>
                        <ListItem key={"info1"} divider>
                            <ListItemAvatar>
                                <Avatar sx={{ background: "transparent" }}>
                                    <WarningAmberOutlinedIcon color='warning' />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText primary={<Typography>Please, check your spam folder in mail. Emails are sent through <Link sx={{ textDecoration: "none" }} href="mailto:noreply@crraoaimscs.ac.in">noreply@crraoaimscs.ac.in</Link>. Report the mail as NOT spam so that you receive our future mails directly in your Inbox. Keep checking this website for updated information.</Typography>} />
                        </ListItem>
                        {false && <ListItem key={"info2"} divider>
                            <ListItemAvatar>
                                <Avatar sx={{ background: "transparent" }}>
                                    <InfoOutlinedIcon color='info' />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText primary={`All the registered students with successful payment must have received login credentials by now.`} />
                        </ListItem>}
                        <ListItem key={"info3"} divider>
                            <ListItemAvatar>
                                <Avatar sx={{ background: "transparent" }}>
                                    <InfoOutlinedIcon color='info' />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText primary={<Typography>Send your queries to <Link sx={{ textDecoration: "none" }} href="mailto:support@crraoaimscs.ac.in">support@crraoaimscs.ac.in</Link></Typography>} />
                        </ListItem>
                        {false && <ListItem key={"info4"} divider>
                            <ListItemAvatar>
                                <Avatar sx={{ background: "transparent" }}>
                                    <InfoOutlinedIcon color='info' />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText primary={`If UPI payment not working on computer browsers, please use mobile phones. If mobile is used, after selection of UPI, select mobile installed app and then try payment. If using desktop, use net-banking or cards payment. RuPay cards are accepted. Visa and Mastercard will be activated soon.`} />
                        </ListItem>}
                        {false && <ListItem key={"info5"} divider>
                            <ListItemAvatar>
                                <Avatar sx={{ background: "transparent" }}>
                                    <CalendarMonthIcon color='primary' />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText primary={`Registration Deadline:`} secondary={
                                <>
                                    <Typography variant='subtitle2'>
                                        For 28<sup>th</sup> January - Open till 27th January 11:59 PM IST
                                    </Typography>
                                    <Typography variant='subtitle2'>
                                        For 3<sup>rd</sup> February - Open till 2nd February 11:59 PM IST
                                    </Typography>
                                    <Typography variant='subtitle2'>
                                        For 4<sup>th</sup> February - Open till 3rd February 11:59 PM IST
                                    </Typography>
                                </>
                            } />
                        </ListItem>}
                    </Alert>
                    <Stack sx={{ border: 1, borderRadius: 3, borderColor: "divider", mb: 1 }}>
                        <Stack sx={{ px: 2, py: 1 }} spacing={2} direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
                            <Typography variant='h6'>Exam summary</Typography>
                            <Tooltip title={"refresh"} arrow placement='top'>
                                <IconButton onClick={() => setRefresh(true)}>
                                    <RefreshRoundedIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                        {exams.length > 0 && <Divider />}
                        {exams
                            .filter(x => x?.id && x?.level && x)
                            .map((exam, index) => (<ListItem key={exam.id} divider={Boolean(index < exams.length - 1)}>
                                <ListItemAvatar>
                                    <Avatar src={"CRRaoAIMSCSLogo512"} sx={{ background: "transparent" }}>
                                        <CalendarMonthIcon color='primary' />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText secondary={`${exam?.name} • Level ${exam.level}`} primary={`${exam.starts && format(new Date(exam.starts), 'PPPpp')} to ${exam.ends && format(new Date(exam.ends), 'p')}`} />
                                <Stack direction={smScreen ? "column" : "row"} spacing={1} justifyContent={"flex-end"} alignItems={"flex-end"} sx={{ ml: 6 }}>
                                    {exam?.type === "PRELIM" && <ListItemIcon>
                                        <Chip
                                            color={exam?.registrationopen ? "success" : "error"}
                                            label={exam?.registrationopen ? "Registration open" : "Registration closed"}
                                        />
                                    </ListItemIcon>}
                                    <ListItemIcon>
                                        <Chip
                                            icon={new Date().getTime() < new Date(exam?.starts).getTime() ? <PendingIcon color='info' /> : new Date().getTime() > new Date(exam?.ends).getTime() ? <CheckCircleIcon color='success' /> : <Skeleton variant="circular" width={20} height={20} sx={{ bgcolor: "#d32f2f" }} />}
                                            label={new Date().getTime() < new Date(exam?.starts).getTime() ? "Upcoming" : new Date().getTime() > new Date(exam?.ends).getTime() ? "Completed" : "Ongoing"}
                                        />
                                    </ListItemIcon>
                                </Stack>
                            </ListItem>))
                        }
                    </Stack>
                    <SocialMedia />
                    <Chip icon={<LinkIcon />} label="https://crraoaimscs.res.in" component={Link} href="https://crraoaimscs.res.in" target={"_blank"} onClick={() => { }} sx={{ textTransform: "none", textDecoration: "none", px: 1 }} />
                    {/* <Box sx={{ p: 2, border: 1, borderRadius: 3, borderColor: "divider" }}>
                        <Instructions />
                    </Box> */}
                    <Box sx={{ p: 2, border: 1, borderRadius: 3, borderColor: "divider" }}>
                        <FinalistInstructions />
                    </Box>
                </Stack>
                {false && <Typography align='center' variant='subtitle2' sx={{ maxWidth: "md", my: 4 }}>
                    An Advanced Institute of Mathematics, Statistics and Computer Science was setup in Hyderabad Central University in 2006
                    and it is befittingly named after the living legend and one of the most celebrated statisticians of our times, Padma Vibhushan
                    C.R. Rao, FRS, Former Director, Indian Statistical Institute, Kolkata, and presently Research Professor at University of Buffalo,
                    USA.
                    In order to raise awareness on statistics, and to encourage those with an aptitude for number and numerical reasoning to study
                    statistics, Prof. C.R.Rao has suggested conducting a Statistics Olympiad to spot statistical talent at an early age on similar lines
                    to the continuing Mathematics Olympiad. C R Rao AIMSCS conducted Statistics Olympiad for the first time in the world in
                    2009 in India and is continuing to do so every year to bring the attention of students the importance of statistics and the need to
                    produce statisticians for national development.

                </Typography>}
            </Stack>
        </Box>
    )
}